import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Icon, Input, Menu, Modal, useToast } from "@astrum/ui";
import type { MenuItem } from "@astrum/ui";
import type { SavedView } from "@astrum/shared";
import { api, ApiError } from "../../lib/api.js";
import { sameView, stateFromSaved, toSavedPayload } from "../../lib/work-views.js";
import type { ViewState } from "../../lib/work-views.js";
import "./task-views.css";

interface Props {
  /** The view as it is on screen right now. */
  current: ViewState;
  /** The project whose views to list; absent on All projects. */
  projectId?: string;
  /** Whether the viewer may save a view for the whole project. The server decides. */
  canShare: boolean;
  /** The saved view the URL names, if any. */
  activeId?: string;
  /** Apply a saved view: the page writes it (and the id) to the URL. */
  onApply: (state: ViewState, id: string | undefined) => void;
}

/**
 * The view switcher: a dropdown of saved views, plus saving the one on screen.
 *
 * Personal views are listed first and are the viewer's own; project views are
 * shared with everyone on the project and marked as such. Applying one writes it
 * into the URL, so the address always describes what is shown — and a link to it
 * reproduces it.
 */
export function SavedViewsMenu({ current, projectId, canShare, activeId, onApply }: Props) {
  const { toast } = useToast();
  const [views, setViews] = useState<SavedView[]>([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    api
      .get<{ views: SavedView[] }>(`/saved-views${projectId ? `?projectId=${projectId}` : ""}`)
      .then((res) => !cancelled && setViews(res.views))
      // Views are a convenience: failing to load them must not block the tasks.
      .catch(() => !cancelled && setViews([]));
    return () => {
      cancelled = true;
    };
  }, [projectId]);
  useEffect(load, [load]);

  const active = views.find((v) => v.id === activeId);
  const modified = !!active && !sameView(current, stateFromSaved(active));

  const fail = (fallback: string) => (error: unknown) =>
    toast(error instanceof ApiError ? error.message : fallback, { tone: "danger" });

  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    api
      .post<{ view: SavedView }>("/saved-views", {
        name: trimmed,
        shared: shared && !!projectId,
        projectId: projectId ?? null,
        ...toSavedPayload(current),
      })
      .then((res) => {
        setViews((prev) => [...prev, res.view]);
        setSaving(false);
        setName("");
        onApply(current, res.view.id);
        toast(`Saved “${res.view.name}”.`);
      })
      .catch(fail("Could not save that view."))
      .finally(() => setBusy(false));
  };

  const update = (view: SavedView) => {
    api
      .patch<{ view: SavedView }>(`/saved-views/${view.id}`, toSavedPayload(current))
      .then((res) => {
        setViews((prev) => prev.map((v) => (v.id === view.id ? res.view : v)));
        toast(`Updated “${view.name}”.`);
      })
      .catch(fail("Could not update that view."));
  };

  const remove = (view: SavedView) => {
    if (!window.confirm(`Delete the view “${view.name}”?${view.shared ? " It is shared with the project." : ""}`)) return;
    api
      .delete(`/saved-views/${view.id}`)
      .then(() => {
        setViews((prev) => prev.filter((v) => v.id !== view.id));
        if (view.id === activeId) onApply(current, undefined);
      })
      .catch(fail("Could not delete that view."));
  };

  const items: MenuItem[] = useMemo(() => {
    const mine = views.filter((v) => !v.shared);
    const team = views.filter((v) => v.shared);
    const entry = (v: SavedView): MenuItem => ({
      id: v.id,
      label: `${v.id === activeId ? "✓ " : ""}${v.name}${v.shared ? " · shared" : ""}`,
      icon: v.shared ? "people" : undefined,
      onSelect: () => onApply(stateFromSaved(v), v.id),
    });
    const list: MenuItem[] = [...mine.map(entry), ...team.map(entry)];
    if (active && modified && active.canEdit) {
      list.push({ id: "update", label: `Update “${active.name}”`, icon: "check", onSelect: () => update(active) });
    }
    if (active && active.canEdit) {
      list.push({ id: "delete", label: `Delete “${active.name}”`, icon: "trash", destructive: true, onSelect: () => remove(active) });
    }
    list.push({
      id: "save",
      label: "Save current view…",
      icon: "plus",
      onSelect: () => {
        setShared(false);
        setSaving(true);
      },
    });
    return list;
    // update/remove close over `current`; rebuilt with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [views, activeId, active, modified, current]);

  return (
    <>
      <Menu open={open} onClose={() => setOpen(false)} label="Saved views" items={items}>
        <Button
          size="sm"
          variant="secondary"
          icon={<Icon name="document" size={14} />}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {active ? `${active.name}${modified ? " (modified)" : ""}` : "Views"}
        </Button>
      </Menu>

      <Modal open={saving} onClose={() => setSaving(false)} title="Save this view">
        <form
          className="tv-save"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <Input label="Name" value={name} maxLength={100} autoFocus onChange={(e) => setName(e.target.value)} />
          <p className="tv-save__hint">
            Saves the layout, filters, sorting and columns you have now. It does not save the tasks themselves.
          </p>
          {projectId && (
            <fieldset className="tv-save__who">
              <legend>Who can see it</legend>
              <label>
                <input type="radio" name="who" checked={!shared} onChange={() => setShared(false)} /> Only me
              </label>
              <label className={canShare ? "" : "is-disabled"}>
                <input type="radio" name="who" checked={shared} disabled={!canShare} onChange={() => setShared(true)} /> Everyone on
                this project
                {!canShare && <span> (needs permission to edit its tasks)</span>}
              </label>
            </fieldset>
          )}
          <div className="tv-save__actions">
            <Button type="button" variant="ghost" onClick={() => setSaving(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={busy} disabled={!name.trim()}>
              Save view
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
